import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useMutation } from '@tanstack/react-query';
import { authApi } from '../api/auth';
import { useInvalidateCurrentUser, useSetCurrentUser } from '../context/UserContext';

const extractErrorMessage = (error) =>
    error?.response?.data?.message || error?.message || 'Something went wrong. Please try again.';

const checkMFARequired = (user, navigate) => {
    if (user?.mfaSecretExists) {
        navigate('/mfa/verify');
    } else {
        navigate('/mfa/setup');
    }
};

export const useSignIn = () => {
    const navigate = useNavigate();
    const setCurrentUser = useSetCurrentUser();
    const [error, setError] = useState('');

    const mutation = useMutation({
        mutationFn: authApi.signIn,
        onSuccess: ({ user, mfaVerified }) => {
            setCurrentUser({ user, mfaVerified });
            setError('');
            if (!mfaVerified) {
                checkMFARequired(user, navigate);
            } else {
                navigate('/');
            }
        },
        onError: (err) => setError(extractErrorMessage(err))
    });

    return { ...mutation, error };
};

export const useGoogleSignIn = () => {
    const navigate = useNavigate();
    const setCurrentUser = useSetCurrentUser();
    const [error, setError] = useState('');

    const redirect = useMutation({
        mutationFn: authApi.googleRedirectUrl,
        onSuccess: (redirectUrl) => {
            window.open(redirectUrl, '_self');
        },
        onError: (err) => setError(extractErrorMessage(err))
    });

    const processCode = useMutation({
        mutationFn: authApi.googleCallback,
        onSuccess: ({ user, mfaVerified }) => {
            setCurrentUser({ user, mfaVerified });
            setError('');
            if (!mfaVerified) {
                checkMFARequired(user, navigate);
            } else {
                navigate('/');
            }
        },
        onError: (err) => setError(extractErrorMessage(err))
    });

    return { redirect, processCode, error };
};

export const useLogout = () => {
    const navigate = useNavigate();
    const invalidate = useInvalidateCurrentUser();
    return useMutation({
        mutationFn: authApi.logout,
        onSettled: () => {
            invalidate();
            navigate('/sign-in');
        }
    });
};

export const useMfaSetupData = () => {
    const [error, setError] = useState('');
    const mutation = useMutation({
        mutationFn: authApi.mfaSetup,
        onError: (err) => setError(extractErrorMessage(err))
    });
    return { ...mutation, error };
};

const CLIENT_IP_KEY = 'admin.mfa.clientIp';

export const useVerifyMfa = () => {
    const navigate = useNavigate();
    const setCurrentUser = useSetCurrentUser();
    const [error, setError] = useState('');

    const mutation = useMutation({
        mutationFn: authApi.mfaVerify,
        onSuccess: ({ user, clientIp }) => {
            setCurrentUser({ user: { ...user, ip: clientIp }, mfaVerified: true });
            setError('');
            if (!user.isWhitelistedIP) {
                sessionStorage.setItem(CLIENT_IP_KEY, clientIp || '');
                navigate('/mfa/ip-whitelist');
            } else {
                sessionStorage.removeItem(CLIENT_IP_KEY);
                navigate('/');
            }
        },
        onError: (err) => setError(extractErrorMessage(err))
    });

    return { ...mutation, error };
};

export const usePendingClientIp = () => sessionStorage.getItem(CLIENT_IP_KEY) || '';

export const useIpWhitelist = () => {
    const navigate = useNavigate();
    const invalidate = useInvalidateCurrentUser();
    const [error, setError] = useState('');
    const mutation = useMutation({
        mutationFn: authApi.ipWhitelist,
        onSuccess: () => {
            sessionStorage.removeItem(CLIENT_IP_KEY);
            invalidate();
            navigate('/');
        },
        onError: (err) => setError(extractErrorMessage(err))
    });
    return { ...mutation, error };
};
